import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { formatPrecise } from '@/lib/format'
import type { UnlicensedArtist } from '@/hooks/useUnlicensedArtists'

interface PresumedUnavailableBrowserProps {
  artists: UnlicensedArtist[]
}

const PAGE_SIZE = 20

// Client-side, same as EssentialsBrowser -- the full list is already one
// fetch (no server pagination), so filtering/paging it in the browser
// avoids a round trip per keystroke or page click.
export function PresumedUnavailableBrowser({ artists }: PresumedUnavailableBrowserProps) {
  const [search, setSearch] = useState('')
  const [tag, setTag] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  // Every tag any listed artist ranks on, most-populated first.
  const tags = useMemo(() => {
    const counts = new Map<string, number>()
    for (const artist of artists) {
      for (const name of Object.keys(artist.themes)) counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  }, [artists])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    let result = artists

    if (tag) {
      // Exact tag (so "punk" doesn't also pull in "pop punk"), best rank
      // within that tag first.
      result = result
        .filter((artist) => tag in artist.themes)
        .sort((a, b) => a.themes[tag] - b.themes[tag])
    }
    if (query) result = result.filter((artist) => artist.name.toLowerCase().includes(query))

    return result
  }, [artists, search, tag])

  // A search that shrinks the result set below the current page would
  // otherwise land on a blank page rather than the top of the new results.
  useEffect(() => {
    setPage(1)
  }, [search, tag])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE

  return (
    <div className="flex flex-col gap-5">
      <div className="relative w-full sm:max-w-xs">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search artist..."
          className="pl-9"
        />
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by tag">
          {tags.map(([name, count]) => (
            <Button
              key={name}
              variant={tag === name ? 'default' : 'outline'}
              size="sm"
              className="h-7 rounded-full px-3 text-xs font-normal"
              aria-pressed={tag === name}
              onClick={() => setTag(tag === name ? null : name)}
            >
              {name}
              <span className={tag === name ? 'opacity-80' : 'text-muted-foreground'}>{count}</span>
            </Button>
          ))}
        </div>
      )}

      {filtered.length > 0 ? (
        <>
          <ul className="flex flex-col gap-3">
            {pageItems.map((artist, index) => (
              <li key={artist.id}>
                <Reveal delay={Math.min(index, 6) * 0.04} y={20}>
                <SpotlightCard className="flex flex-col gap-3 rounded-2xl p-5">
                  <div className="flex items-start gap-4">
                    <span className="w-12 shrink-0 font-display text-4xl font-semibold leading-none tracking-tighter text-outline">
                      {String(startIndex + index + 1).padStart(2, '0')}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-xl font-semibold tracking-tight">{artist.name}</span>
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {artist.theme_count} themes
                        </span>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        Best rank #{artist.best_rank} &middot; avg #{artist.avg_rank}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 sm:pl-16">
                    {Object.entries(artist.themes)
                      .sort((a, b) => a[1] - b[1])
                      .map(([theme, rank]) => (
                        <Badge key={theme} variant={theme === tag ? 'default' : 'secondary'} className="gap-1 font-normal">
                          {theme}
                          <span className={theme === tag ? 'opacity-80' : 'text-muted-foreground'}>#{rank}</span>
                        </Badge>
                      ))}
                  </div>
                </SpotlightCard>
                </Reveal>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
            <span className="text-xs text-muted-foreground">
              {formatPrecise(filtered.length)} {filtered.length === 1 ? 'artist' : 'artists'}
              {filtered.length !== artists.length ? ` (of ${formatPrecise(artists.length)})` : ''}
            </span>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="text-xs tabular-nums text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No artists match your search.</p>
      )}
    </div>
  )
}
